import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	Snackbar,
	TextField,
	IconButton,
	FormControl,
	InputLabel,
	OutlinedInput,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Alert } from "@mui/lab";
import { useFormsContext } from "@setup/configurations/forms/context/FormsContext";
import {
	ErrorModal,
	AssetTreePicker,
	useAssetContext,
	TimeService,
	getMomentTz,
} from "@dexteel/mesf-core";
import type { FormAsset } from "@setup/configurations/forms/models/FormAsset";
import { upsertFormAsset } from "@setup/configurations/forms/repositories/FormsRepository";
import { ScheduleCodePicker } from "../picker/ScheduleCodePicker";
import { INITIAL_VALUES_FORM_ASSET } from "@setup/configurations/forms/models/FormAssetInitialValue";
import { DateTimePicker } from "@mui/x-date-pickers";
import EditIcon from "@mui/icons-material/Edit";
import cronstrue from "cronstrue";

const moment = getMomentTz();

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	checkbox: {
		userSelect: "none",
	},
	assetPicker: {
		"& .MuiGrid-item": {
			width: "500px",
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	inputEditable: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},
	comments: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
	cronField: {
		"& .MuiInputBase-root": {
			height: "40px",
		},
		"& .MuiOutlinedInput-input": {
			padding: "10px 14px",
		},
	},
	scheduleFormControl: {
		width: "100%",
		"& .MuiInputBase-root": {
			height: "40px",
		},
		"& .MuiOutlinedInput-input": {
			padding: "10px 14px",
		},
		"& .MuiInputLabel-outlined": {
			transform: "translate(14px, 12px) scale(1)",
		},
		"& .MuiInputLabel-shrink": {
			transform: "translate(14px, -6px) scale(0.75)",
		},
	},
	disabledDatePicker: {
		"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
			borderWidth: "1px !important",
			borderColor: "rgba(0, 0, 0, 0.23)",
		},
		"& .MuiInputBase-input.Mui-disabled": {
			color: "rgba(0, 0, 0, 0.38)",
		},
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			borderWidth: "1px",
		},
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
	formRow: {
		display: "flex",
		flexWrap: "wrap",
		marginTop: 10,
		width: "100%",
	},
	dialogContentFixed: {
		maxHeight: "60vh",
		overflowY: "auto",
		"@media (max-height: 600px)": {
			maxHeight: "50vh",
		},
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const NewFormAssetModal = ({ show, onHide }: Props) => {
	const classes = useStyles();
	const [addSuccess, setAddSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const {
		state: {
			FormName,
			FormId,
			userFormAssetData: { AssetId, AssetName },
		},
		actions: {
			setAssetId,
			setAssetName,
			setFormAssetFormAssetId,
			setFormAssetAssetId,
		},
	} = useFormsContext();

	const {
		state: { allAssets },
	} = useAssetContext();

	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [cronExpression, setCronExpression] = useState<string>("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isCustomSchedule, setIsCustomSchedule] = useState(false);

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { isValid, errors },
		reset,
	} = useForm<FormAsset>({
		defaultValues: INITIAL_VALUES_FORM_ASSET,
	});

	const schedulePolicyCode = watch("SchedulePolicyCode");

	const handleCancel = async () => {
		reset();
		setAssetId(0);
		setAssetName("Plant");
		onHide(false);
	};

	const changeSearchAsset = (assetId: number | null) => {
		const id = assetId || 0;
		setValue("AssetId", id);
		setAssetId(id);
		setFormAssetAssetId(id);

		// Find asset name
		const foundAsset = allAssets.find((asset) => asset.AssetId === id);
		if (foundAsset) {
			setAssetName(foundAsset.AssetName);
		}
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAddSuccess(false);
	};

	const onSubmit = async (data: FormAsset) => {
		setIsSubmitLoading(true);

		const submitData = {
			...data,
			CronExpression: cronExpression,
		};

		const res = await upsertFormAsset(submitData);
		if (res.ok) {
			setFormAssetFormAssetId(null);
			setIsSubmitLoading(false);
			setAddSuccess(true);
			reset();
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	const handleScheduleStartChange = (date: Date) => {
		setValue("StartDate", date);
	};

	useEffect(() => {
		if (show) {
			reset();
			setAssetId(0);
			setAssetName("Plant");
			setValue("AssetId", 0);
			setValue("FormId", FormId);
			setValue("StartDate", new Date());
			setIsSubmitLoading(false);
			setCronExpression("");
			setIsCustomSchedule(false);
		}
	}, [show]);

	useEffect(() => {
		setIsCustomSchedule(schedulePolicyCode === "P");
	}, [schedulePolicyCode]);

	return (
		<>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container style={{ padding: "15px 0 20px" }}>
						<DialogTitle
							style={{
								padding: "5px 40px 10px 24px",
								marginRight: 20,
								width: "100%",
							}}
						>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<h1 style={{ fontSize: "1em" }}>FORM: {FormName}</h1>
							</Grid2>
						</DialogTitle>
						<Grid2 container>
							<DialogContent
								dividers
								style={{ width: "100%", padding: "20px" }}
								className={classes.dialogContentFixed}
							>
								<Grid2 container>
									<Grid2
										style={{ paddingLeft: 0, marginBottom: 15 }}
										className={classes.inputEditable}
										size={{ md: 12, xs: 12 }}
									>
										<Controller
											name="AssetId"
											control={control}
											render={({ field: { value } }) => (
												<AssetTreePicker
													label="Asset"
													value={value || 0}
													onChange={changeSearchAsset}
												/>
											)}
										/>
									</Grid2>

									<div className={classes.formRow}>
										<Grid2
											style={{ paddingRight: 10 }}
											className={classes.inputEditable}
											size={{ md: 4, xs: 12 }}
										>
											<Controller
												name="SchedulePolicyCode"
												control={control}
												render={({ field: { onChange, value } }) => (
													<ScheduleCodePicker
														value={value as string}
														onChange={onChange}
														cronExpression={cronExpression}
														onCronExpressionChange={setCronExpression}
														isModalOpen={isModalOpen}
														setIsModalOpen={setIsModalOpen}
														hideScheduleStart={setIsCustomSchedule}
														onScheduleStartChange={handleScheduleStartChange}
													/>
												)}
											/>
										</Grid2>

										<Grid2
											style={{ paddingRight: 10 }}
											className={classes.inputEditable}
											size={{ md: 2, xs: 12 }}
										>
											<Controller
												name="Periods"
												control={control}
												rules={{ required: true, pattern: /^[0-9]*$/ }}
												render={({
													field: { onChange, value },
													fieldState: { error },
												}) => (
													<TextField
														className={classes.root}
														margin="dense"
														label="Periods"
														variant="outlined"
														fullWidth
														autoComplete="off"
														value={value}
														onChange={(evt) => {
															const newValue = evt.target.value.replace(
																/[^0-9]/g,
																"",
															);
															if (newValue.length <= 99) onChange(newValue);
														}}
														error={!!error}
														helperText={error ? "Enter a period number" : ""}
														sx={{
															marginTop: "8px",
															"& .MuiInputBase-root": {
																height: "40px",
															},
															"& .MuiOutlinedInput-input": {
																padding: "10px 14px",
															},
														}}
													/>
												)}
											/>
										</Grid2>

										<Grid2
											className={classes.inputEditable}
											size={{ md: 6, xs: 12 }}
										>
											<Controller
												name="StartDate"
												control={control}
												render={({ field }) => (
													<DateTimePicker
														label="Start Date"
														timezone={
															isCustomSchedule
																? "UTC"
																: TimeService.getInstance().getServerTimeZone()
														}
														format={
															isCustomSchedule
																? "MM/DD/YYYY"
																: "MM/DD/YYYY HH:mm"
														}
														timeSteps={{ minutes: 1 }}
														value={moment(field.value) as any}
														onChange={field.onChange}
														inputRef={field.ref}
														disabled={isCustomSchedule}
														slotProps={{
															textField: {
																fullWidth: true,
																variant: "outlined",
																margin: "dense",
																error: !!errors.StartDate,
																className: classes.disabledDatePicker,
																sx: {
																	marginTop: "8px",
																	"& .MuiInputBase-root": {
																		height: "40px",
																	},
																	"& .MuiInputBase-input": {
																		padding: "10px 14px",
																	},
																},
															},
														}}
													/>
												)}
											/>
										</Grid2>
									</div>

									{isCustomSchedule && (
										<Grid2
											className={classes.inputEditable}
											style={{ marginTop: "8px" }}
											size={{ md: 12, xs: 12 }}
										>
											<FormControl
												variant="outlined"
												margin="dense"
												className={classes.scheduleFormControl}
											>
												<InputLabel>Schedule</InputLabel>
												<OutlinedInput
													placeholder="Choose your schedule"
													value={
														cronExpression
															? cronstrue.toString(cronExpression)
															: ""
													}
													readOnly={true}
													label="Schedule"
													endAdornment={
														<IconButton
															edge="end"
															onClick={() => setIsModalOpen(true)}
															size="small"
														>
															<EditIcon />
														</IconButton>
													}
												/>
											</FormControl>
										</Grid2>
									)}
								</Grid2>
							</DialogContent>
							<Grid2
								justifyContent="flex-end"
								style={{ marginTop: "15px" }}
								size={{ md: 12, xs: 12 }}
							>
								<DialogActions style={{ padding: "0px 24px" }}>
									<Grid2
										container
										justifyContent="flex-end"
										spacing={2}
										style={{ margin: 0 }}
									>
										<Grid2 size={{ md: 3, xs: 4 }}>
											<Button
												fullWidth
												variant="contained"
												color="inherit"
												onClick={handleCancel}
											>
												Cancel
											</Button>
										</Grid2>
										<Grid2 style={{ paddingRight: 0 }} size={{ md: 3, xs: 4 }}>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={
													!isValid ||
													isSubmitLoading ||
													(schedulePolicyCode === "P" &&
														(!cronExpression ||
															cronExpression.trim().length === 0))
												}
												variant="contained"
												color="primary"
												type="submit"
											>
												Save
											</Button>
										</Grid2>
									</Grid2>
								</DialogActions>
							</Grid2>
						</Grid2>
					</Grid2>
				</form>
			</Dialog>
			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={addSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
